/**
 * Filtering + ordering for the open board, applied to the page the server returned.
 *
 * Why client-side: the server already narrows the feed to what the viewer may take on (not mine,
 * not taken, not started, affordable) and returns one page. Rank, entry band and slate size then
 * only reorder/hide within that page, so doing it here makes a chip tap instant instead of a
 * round-trip. The trade-off is that filters see one page, not the whole board — the caller shows
 * the resulting count so "3 open challenges" is always the truth about what is on screen.
 *
 * specs/open-challenges AC-17.
 */

import {
  OPEN_BOARD_QUICK_MAX_PICKS,
  OPEN_BOARD_URGENT_MINUTES,
} from '../constants/openBoard.constants';
import type {
  OpenBoardBrowseSort,
  OpenBoardBrowseState,
  OpenBoardCard,
} from '../types/openBoard.types';

/** Minutes until the first kickoff; null when the card has no usable start date. */
export function minutesToKickoff(startDate: string | null | undefined, now: number): number | null {
  if (!startDate) return null;
  const ms = new Date(startDate).getTime();
  if (!Number.isFinite(ms)) return null;
  return Math.round((ms - now) / 60000);
}

/** Closing soon = kickoff within the urgency window (and not already past). */
export function isClosingSoon(card: OpenBoardCard, now: number): boolean {
  const mins = minutesToKickoff(card.startDate, now);
  return mins != null && mins >= 0 && mins <= OPEN_BOARD_URGENT_MINUTES;
}

function matchesRank(card: OpenBoardCard, rankFloor: number | null): boolean {
  if (rankFloor == null) return true;
  const rank = card.creator?.rank;
  // Unknown rank is excluded from a rank-floored view: including it would let an unranked poster
  // masquerade as meeting a floor the viewer explicitly asked for.
  return typeof rank === 'number' && rank >= rankFloor;
}

function matchesStake(card: OpenBoardCard, min: number | null, max: number | null): boolean {
  if (min != null && card.stakeCoins < min) return false;
  if (max != null && card.stakeCoins > max) return false;
  return true;
}

/** Slate size, measured on the challenge itself: quick = within the free allowance, big = past it. */
function matchesSize(card: OpenBoardCard, size: OpenBoardBrowseState['size']): boolean {
  if (size === 'all') return true;
  const isBig = card.matchCount > OPEN_BOARD_QUICK_MAX_PICKS;
  return size === 'big' ? isBig : !isBig;
}

const SORTERS: Record<OpenBoardBrowseSort, (a: OpenBoardCard, b: OpenBoardCard) => number> = {
  // A card with no start date sorts last rather than first — an unknown kickoff is not "imminent".
  'closing-soonest': (a, b) => startMs(a) - startMs(b),
  'highest-stake': (a, b) => b.stakeCoins - a.stakeCoins,
  'top-rank': (a, b) => (b.creator?.rank ?? 0) - (a.creator?.rank ?? 0),
  'most-picks': (a, b) => b.matchCount - a.matchCount,
};

function startMs(card: OpenBoardCard): number {
  if (!card.startDate) return Number.POSITIVE_INFINITY;
  const ms = new Date(card.startDate).getTime();
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
}

/**
 * Apply the browse selection to a page of board cards.
 * Pure: never mutates the input array, so React Query's cached page stays intact.
 */
export function browseOpenBoard(
  cards: readonly OpenBoardCard[],
  browse: OpenBoardBrowseState
): OpenBoardCard[] {
  const filtered = cards.filter(
    (c) =>
      matchesRank(c, browse.rankFloor) &&
      matchesStake(c, browse.minStake, browse.maxStake) &&
      matchesSize(c, browse.size)
  );
  // Ties keep the server's order (closing-soonest), so equal stakes/ranks still read sensibly.
  return filtered.slice().sort(SORTERS[browse.sort] ?? SORTERS['closing-soonest']);
}

/** How many constraints are active — drives the badge on the filter button. */
export function countActiveFilters(browse: OpenBoardBrowseState): number {
  let n = 0;
  if (browse.rankFloor != null) n += 1;
  if (browse.minStake != null || browse.maxStake != null) n += 1;
  if (browse.size !== 'all') n += 1;
  return n;
}
